import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './Concept.css';
import { Header } from '../../components/header/Header';
import { MobileFooter } from '../../components/mobileFooter/MobileFooter';
import erledixLogo from '../../assets/erledix-logo-base-bg.png';

// Detect if device is iOS
const isIOS = () => {
	if (typeof navigator === 'undefined') return false;
	return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Wrapper component for motion.div that disables animations on iOS
interface AnimatedDivProps {
	children: React.ReactNode;
	className?: string;
	initial?: any;
	animate?: any;
	whileInView?: any;
	transition?: any;
	viewport?: any;
	ref?: React.Ref<HTMLDivElement>;
	style?: React.CSSProperties;
}

const AnimatedDiv = React.forwardRef<HTMLDivElement, AnimatedDivProps>(
	({ children, className, initial, animate, whileInView, transition, viewport, style }, ref) => {
		const [isIOSDevice] = useState(() => isIOS());

		if (isIOSDevice) {
			return (
				<div ref={ref} className={className} style={style}>
					{children}
				</div>
			);
		}

		return (
			<motion.div
				ref={ref}
				className={className}
				initial={initial}
				animate={animate}
				whileInView={whileInView}
				transition={transition}
				viewport={viewport}
				style={style}
			>
				{children}
			</motion.div>
		);
	}
);

AnimatedDiv.displayName = 'AnimatedDiv';

const ParentIcon = () => (
	<svg viewBox='0 0 24 24' fill='white'>
		<path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/>
	</svg>
);

const ChildIcon = () => (
	<svg viewBox='0 0 24 24' fill='white'>
		<path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/>
	</svg>
);


export const ConceptPage = () => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: '-100px' });

	return (
		<div>
			<Header style={{ backgroundColor: '#ffffff' }} />
			<div className='concept-page'>
				<AnimatedDiv
					ref={ref}
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ duration: 0.5, ease: 'easeOut' }}
					className='concept-container'
				>
					<h1>Das Konzept von Erledix</h1>

					{/* Intro Section */}
					<section className='concept-section intro-section'>
						<div className='section-header'>
							<img src={erledixLogo} alt='Erledix Logo' className='erledix-logo' />
							<h2>Was ist Erledix?</h2>
						</div>
						<div className='intro-content'>
							<p>
								Erledix ist eine spielerische To-Do-App für Familien. Sie hilft
								Kindern dabei, Aufgaben im Alltag zu übernehmen, und unterstützt
								Eltern dabei, den Familienalltag entspannter zu organisieren.
							</p>
							<p>
								Kinder erledigen Aufgaben, sammeln dafür Münzen und können diese
								anschließend gegen zuvor festgelegte Belohnungen eintauschen. So
								werden alltägliche Aufgaben mit weniger Diskussionen erledigt und
								es bleibt mehr Zeit für schöne gemeinsame Momente.
							</p>
							<p>
								Gleichzeitig lernen Kinder, Verantwortung zu übernehmen, auf Ziele
								hinzuarbeiten und dranzubleiben.
							</p>
						</div>
					</section>

					{/* Profile Section */}
					<section className='concept-section'>
						<div className='section-header'>
							<h2>Profile bei Erledix</h2>
						</div>
						<p className='section-intro'>
							Bei Erledix gibt es zwei Arten von Profilen, um eine sichere und
							familiengerechte Nutzung zu ermöglichen: Eltern-/Erwachsenenprofile und
							Kinderprofile.
						</p>

						<motion.div
							className='support-info-box'
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							viewport={{ once: true }}
						>
							<div className='support-info-content'>
								<div className='support-info-text'>
									<h3>Anleitung: Profile einrichten</h3>
									<p>
										Erfahre, wie du dein Elternprofil erstellst und Kinderprofile
										hinzufügst.
									</p>
								</div>
								<a href='/support#registration' className='support-button'>
									Zur Anleitung
								</a>
							</div>
						</motion.div>

						<div className='profile-grid'>
							<motion.div
								className='profile-card'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								viewport={{ once: true }}
							>
								<div className='profile-card-header'>
									<div className='profile-icon parent-icon'>
										<ParentIcon />
									</div>
									<h3>Eltern- / Erwachsenenprofile</h3>
								</div>
								<p>
									Ein Eltern- oder Erwachsenenprofil wird vollständig registriert
									und kann alle Funktionen der App nutzen. Dieses Profil verwaltet
									Inhalte, Gruppen und Kinderprofile.
								</p>
								<p className='profile-subtitle'>
									<strong>Eltern-/Erwachsenenprofile können:</strong>
								</p>
								<ul>
									<li>Gruppen erstellen und beitreten</li>
									<li>Kinderprofile hinzufügen und betreuen</li>
									<li>Einstellungen und Zugriffsrechte steuern</li>
								</ul>
							</motion.div>

							<motion.div
								className='profile-card'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.1 }}
								viewport={{ once: true }}
							>
								<div className='profile-card-header'>
									<div className='profile-icon child-icon'>
										<ChildIcon />
									</div>
									<h3>Kinderprofile</h3>
								</div>
								<p>
									Da nicht jedes Kind eine eigene E-Mail-Adresse besitzt oder sein
									Profil selbst verwalten soll, gibt es bei Erledix Kinderprofile.
								</p>
								<p className='profile-subtitle'>
									<strong>Kinderprofile:</strong>
								</p>
								<ul>
									<li>benötigen keine eigene Registrierung</li>
									<li>
										sind immer mit mindestens einem Eltern-/Erwachsenenprofil
										verknüpft
									</li>
									<li>
										werden vollständig von den verbundenen Erwachsenen verwaltet
									</li>
								</ul>
								<p>
									Ein Kinderprofil kann von mehreren Elternteilen gemeinsam
									verwaltet werden. Außerdem kann ein Kinderprofil auf einem
									eigenen Gerät genutzt werden, die Kontrolle bleibt dennoch
									jederzeit bei den verknüpften Erwachsenen.
								</p>
								<p className='profile-subtitle'>
									<strong>Was ein Kinderprofil kann:</strong>
								</p>
								<ul>
									<li>
										die zugeteilte Rolle innerhalb einer Gruppe vollständig
										nutzen
									</li>
									<li>Feedback zur App geben</li>
								</ul>
								<p className='profile-subtitle'>
									<strong>Was ein Kinderprofil nicht kann:</strong>
								</p>
								<ul>
									<li>den eigenen Account löschen</li>
									<li>
										selbst Gruppen über Einladungscodes beitreten (das
										Hinzufügen erfolgt durch Eltern)
									</li>
									<li>eigene Kinderprofile erstellen</li>
									<li>Profildaten selbst bearbeiten</li>
									<li>Einladungscodes erzeugen</li>
									<li>Gruppen erstellen</li>
									<li>Sicherheitsanfragen erstellen</li>
								</ul>
							</motion.div>
						</div>
					</section>

					{/* Groups Section */}
					<section className='concept-section'>
						<div className='section-header'>
							<h2>Gruppen bei Erledix</h2>
						</div>
						<div className='section-content'>
							<p>
								Gruppen sind das Herzstück von Erledix. Innerhalb einer Gruppe
								werden Aufgaben, Belohnungen und Münzen organisiert.
							</p>
							<p>
								Eltern-/Erwachsenenprofile können mehrere Gruppen erstellen, zum
								Beispiel für:
							</p>
							<ul className='content-list'>
								<li>verschiedene Familien</li>
								<li>unterschiedliche Haushalte</li>
								<li>spezielle Anwendungsfälle</li>
							</ul>
							<p>
								Aufgaben, Belohnungen und verdiente Münzen sind immer
								gruppengebunden. So bleibt alles übersichtlich und klar getrennt.
							</p>
							<p>
								Kinderprofile können direkt und ohne Einladungscode zu Gruppen
								hinzugefügt werden. Andere Erwachsene werden über einen
								Einladungscode eingeladen.
							</p>
							<p className='highlight-box'>
								<strong>Wichtig:</strong> Jedes Gruppenmitglied erhält innerhalb der
								Gruppe eine Rolle, unabhängig von der Profilart.
							</p>
						</div>

						<motion.div
							className='support-info-box'
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							viewport={{ once: true }}
						>
							<div className='support-info-content'>
								<div className='support-info-text'>
									<h3>Anleitung: Gruppen erstellen & verwalten</h3>
									<p>
										Lerne, wie du Gruppen erstellst, Mitglieder hinzufügst und
										Einladungscodes verwendest.
									</p>
								</div>
								<a href='/support#groups' className='support-button'>
									Zur Anleitung
								</a>
							</div>
						</motion.div>
					</section>

					{/* Roles Section */}
					<section className='concept-section'>
						<div className='section-header'>
							<h2>Rollen bei Erledix</h2>
						</div>
						<p className='section-intro'>
							Rollen bestimmen, was ein Mitglied innerhalb einer Gruppe tun darf. Sie
							sind gruppenbezogen und unabhängig vom Profiltyp (Eltern oder Kind).
						</p>

						<div className='roles-grid'>
							<motion.div
								className='role-card'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								viewport={{ once: true }}
							>
								<div className='role-badge'>Bearbeiter</div>
								<h3>Aufgaben erledigen</h3>
								<ul>
									<li>kann Aufgaben abschließen</li>
									<li>kann Belohnungen einfordern</li>
									<li>sammelt Münzen</li>
								</ul>
							</motion.div>

							<motion.div
								className='role-card'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.1 }}
								viewport={{ once: true }}
							>
								<div className='role-badge creator'>Ersteller</div>
								<h3>Aufgaben verwalten</h3>
								<ul>
									<li>kann Aufgaben und Belohnungen erstellen und bearbeiten</li>
									<li>
										kann Aufgabenabschlüsse und Belohnungsforderungen bestätigen
										oder ablehnen
									</li>
									<li>sammelt selbst keine Münzen</li>
								</ul>
							</motion.div>

							<motion.div
								className='role-card'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.2 }}
								viewport={{ once: true }}
							>
								<div className='role-badge admin'>Verwalter</div>
								<h3>Alles verwalten</h3>
								<ul>
									<li>hat alle Rechte des Erstellers</li>
									<li>kann Gruppen verwalten</li>
									<li>kann Mitglieder einladen oder entfernen</li>
									<li>kann Gruppeninformationen bearbeiten</li>
								</ul>
							</motion.div>
						</div>

						<div className='roles-info'>
							<p className='highlight-box'>
								<strong>Wichtig:</strong> Nur Bearbeiter können Münzen sammeln.
								Ersteller und Verwalter sammeln keine Münzen, da sie Aufgaben und
								Belohnungen verwalten.
							</p>
							<p>
								Ein Kind kann, wenn Eltern dies bewusst festlegen,auch eine
								höhere Rolle (z. B. Verwalter) innerhalb einer Gruppe übernehmen.
								Die Standardrolle für Kinder ist jedoch der Bearbeiter.
							</p>
						</div>

						{/* <motion.div
							className='support-info-box'
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
							viewport={{ once: true }}
						>
							<div className='support-info-content'>
								<div className='support-info-text'>
									<h3>Anleitung: Rollen & Berechtigungen</h3>
									<p>
										Erfahre, wie du Rollen zuweist und welche Berechtigungen jede
										Rolle hat.
									</p>
								</div>
								<a href='/support#groups' className='support-button'>
									Zur Anleitung
								</a>
							</div>
						</motion.div> */}
					</section>

					{/* Tasks & Rewards Section */}
					<section className='concept-section'>
						<div className='section-header'>
							<h2>Das Belohnungsystem bei Erledix</h2>
						</div>
						<div className='section-content'>
							<p>
								Innerhalb einer Gruppe können Aufgaben und Belohnungen individuell
								erstellt werden.
							</p>
							<p>
								<strong>Aufgaben</strong> sind alltägliche Tätigkeiten, für die eine
								bestimmte Anzahl an Münzen vergeben wird.
							</p>
							<p>
								<strong>Belohnungen</strong> können gegen gesammelte Münzen
								eingelöst werden.
							</p>
							<p>
								Der Abschluss von Aufgaben und das fordern von Belohnungen ist immer
								eine <strong>zweiphasige</strong> Aktion: Zunächst wird die
								Aufgabe/Belohnung beantragt und muss dann von einem Ersteller oder
								Verwalter bestätigt werden. Wird der Abschluss einer Aufgabe oder
								die forderung einer Belohung abgelehnt, ist keine Menge an Münzen
								verloren, da diese erst nach der Bestätigung des
								Erstellers/Verwalters vergeben werden, der Bearbeiter kann es
								nochmal versuchen.
							</p>
							<p>
								Die Sichtbarkeit von Aufgaben und Belohnungen kann so eingestellt
								werden, dass sie nur bestimmten Gruppenmitgliedern angezeigt werden.
							</p>

							<p>
								<strong>Münzen</strong> werden durch das Abschließen von Aufgaben
								verdient. Sie können ausschließlich gegen Belohnungen eingetauscht
								werden. Münzen sind immer gruppengebunden und nur in der Gruppe
								einlösbar, in der sie verdient wurden.
							</p>
							<p>So bleibt das System fair, transparent und leicht verständlich.</p>
						</div>

						<div className='support-info-grid'>
							<motion.div
								className='support-info-box'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								viewport={{ once: true }}
							>
								<div className='support-info-content'>
									<div className='support-info-text'>
										<h3>Anleitung: Aufgaben erstellen</h3>
										<p>
											Lerne, wie du Aufgaben erstellst, Münzen vergibst und
											Aufgabenabschlüsse bestätigst.
										</p>
									</div>
									<a href='/support#tasks' className='support-button'>
										Zur Anleitung
									</a>
								</div>
							</motion.div>

							<motion.div
								className='support-info-box'
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.1 }}
								viewport={{ once: true }}
							>
								<div className='support-info-content'>
									<div className='support-info-text'>
										<h3>Anleitung: Belohnungen verwalten</h3>
										<p>
											Erfahre, wie du Belohnungen erstellst und Anfragen
											verwaltest.
										</p>
									</div>
									<a href='/support#rewards' className='support-button'>
										Zur Anleitung
									</a>
								</div>
							</motion.div>
						</div>
					</section>
				</AnimatedDiv>
			</div>
			<MobileFooter />
		</div>
	);
};
